import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Clock3, TrendingUp, Music2, Radio } from "lucide-react";
import { Link } from "react-router-dom";
import DraftCard from "@/components/DraftCard";
import GenerateDialog from "@/components/GenerateDialog";

export default function Dashboard() {
  const [drafts, setDrafts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genOpen, setGenOpen] = useState(false);

  const refresh = async () => {
    const [d, m, r] = await Promise.all([
      api.get("/drafts"),
      api.get("/metrics"),
      api.get("/releases"),
    ]);
    setDrafts(d.data);
    setMetrics(m.data);
    setReleases(r.data);
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const pollUntilDone = async ({ job_id, total }) => {
    setGenerating(true);
    let done = false;
    for (let i = 0; i < 80 && !done; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const s = await api.get(`/drafts/generate/${job_id}`);
        await refresh();
        if (s.data.status === "completed") {
          done = true;
          toast.success(`Generated ${s.data.done}/${total} drafts.`);
        } else if (s.data.status === "failed") {
          done = true;
          toast.error("Generation failed.");
        }
      } catch {/* transient */}
    }
    setGenerating(false);
  };

  const generate = async () => {
    setGenOpen(true);
  };

  const pendingDrafts = useMemo(
    () => drafts.filter((d) => d.status === "draft").slice(0, 4),
    [drafts]
  );
  const scheduled = useMemo(
    () => drafts.filter((d) => d.status === "scheduled").slice(0, 3),
    [drafts]
  );

  const latest = releases[0];

  return (
    <div className="fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <div className="label-mono mb-4">Overview / this week</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
            The agent is <span className="italic text-white/85">listening</span>.
          </h1>
          <p className="text-white/55 mt-4 max-w-xl">
            Your next release: <span className="text-white">{latest?.title || "—"}</span>.
            Trigger a week of content, then approve what goes live.
          </p>
        </div>
        <button
          className="btn-amber whitespace-nowrap disabled:opacity-60"
          onClick={generate}
          disabled={generating}
          data-testid="generate-batch-btn"
        >
          <Sparkles size={14} />
          {generating ? "Generating…" : "Generate Weekly Batch"}
        </button>
      </div>

      <GenerateDialog open={genOpen} onOpenChange={setGenOpen} onStarted={pollUntilDone} />

      {/* Bento grid */}
      {loading ? (
        <div className="label-mono">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Metrics — col 4 */}
          <section className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-6">
            <MetricCard
              label="Posts live"
              value={metrics?.posted_count ?? 0}
              Icon={CheckCircle2}
              testid="metric-posted"
            />
            <MetricCard
              label="Total reach"
              value={(metrics?.total_reach ?? 0).toLocaleString()}
              Icon={TrendingUp}
              testid="metric-reach"
            />
            <MetricCard
              label="Scheduled"
              value={metrics?.by_status?.scheduled ?? 0}
              Icon={Clock3}
              testid="metric-scheduled"
            />
            <MetricCard
              label="Drafts pending"
              value={metrics?.by_status?.draft ?? 0}
              Icon={Radio}
              testid="metric-draft"
            />
          </section>

          {/* Latest release feature — col 8 */}
          <section className="md:col-span-8 panel overflow-hidden relative">
            {latest?.cover_url && (
              <div
                className="absolute inset-0 opacity-50"
                style={{
                  backgroundImage: `url('${latest.cover_url}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: "grayscale(0.3) contrast(1.05)",
                }}
              />
            )}
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(135deg, rgba(3,3,3,0.95) 0%, rgba(3,3,3,0.55) 60%, rgba(3,3,3,0.3) 100%)" }}
            />
            <div className="relative z-10 p-8 md:p-12 min-h-[360px] flex flex-col justify-between">
              <div>
                <div className="label-mono">Focus release</div>
                <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight max-w-xl">
                  {latest?.title || "No release detected"}
                </h2>
                <div className="flex items-center gap-3 mt-5">
                  <span className="badge">{latest?.source?.replace("_", " ")}</span>
                  <span className="label-mono">{latest?.release_date}</span>
                </div>
              </div>
              <div className="flex gap-3 mt-10">
                <Link to="/queue" className="btn-ghost-line" data-testid="dashboard-open-queue">
                  Review drafts
                </Link>
                <Link to="/releases" className="btn-ghost-line" data-testid="dashboard-open-releases">
                  All releases
                </Link>
              </div>
            </div>
          </section>

          {/* Approval queue preview — col 8 */}
          <section className="md:col-span-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-mono">Approval queue</div>
                <h3 className="font-display text-2xl mt-1">Awaiting your call</h3>
              </div>
              <Link to="/queue" className="label-mono hover:text-white" data-testid="view-all-drafts">
                VIEW ALL →
              </Link>
            </div>
            {pendingDrafts.length === 0 ? (
              <div className="panel p-10 text-center">
                <Music2 className="mx-auto text-white/20 mb-3" size={32} />
                <div className="text-white/55 text-sm">
                  No drafts waiting. Generate a weekly batch to get started.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 fade-in-stagger">
                {pendingDrafts.map((d) => (
                  <DraftCard key={d.draft_id} draft={d} onChange={refresh} compact />
                ))}
              </div>
            )}
          </section>

          {/* Scheduled preview — col 4 */}
          <section className="md:col-span-4 panel p-6">
            <div className="label-mono mb-1">Next up</div>
            <h3 className="font-display text-2xl mb-5">Scheduled</h3>
            {scheduled.length === 0 ? (
              <div className="text-white/45 text-sm">Nothing scheduled yet.</div>
            ) : (
              <ul className="space-y-4">
                {scheduled.map((s) => (
                  <li key={s.draft_id} className="border-l border-[#FF3B00]/50 pl-4">
                    <div className="label-mono">{new Date(s.scheduled_at).toLocaleString()}</div>
                    <div className="text-sm mt-1">{s.release_title}</div>
                    <div className="font-mono text-[11px] text-white/50 mt-0.5 uppercase tracking-wider">
                      {s.platform} · {s.content_type}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, Icon, testid }) {
  return (
    <div className="panel p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-6">
        <div className="label-mono">{label}</div>
        <Icon size={14} className="text-white/30" strokeWidth={1.5} />
      </div>
      <div className="font-display text-4xl md:text-5xl tracking-tight">{value}</div>
    </div>
  );
}
